import { FormControl, FormGroup, SxProps } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import { PREDICAMENT_STATE_NO, PREDICAMENT_STATE_YES } from "core/consts";
import { GUARDIAN_1, GUARDIAN_2 } from "core/schemas/GKVGeneralFormSchema";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import { SubheadingFormLegend } from "ds_legacy/components/FormComponents/SubheadingFormLegend";
import RadioGroupV2 from "ds_legacy/components/RadioGroupV2";
import { RADIO_WHITE_BACKGROUND } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_18,
  FONT_WEIGHT_BOLD,
  Subheading,
} from "ds_legacy/materials/typography";
import {
  DatePickerWithLabel,
  FormSectionsWrapper,
  PatientFormsSectionHeader,
  PatientFormSubheading,
  patientSubheadingSx,
  Section,
  SectionRow,
  SMALL_INPUT_MIN_WIDTH,
  SMALL_INPUT_WIDTH,
  TextAreaWithLabel,
  TextInputWithLabel,
  YesNoRadioWithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { FormElementRenderProps } from "react-forms-state/src";
import { useTranslations } from "translations";
import { useGuardianOptions, useLivingSituationOptions } from "./hooks";
import { DownloadIcon } from "lucide-react";
import { Auction } from "core/types";
import { useStateDispatcherContext } from "react-forms-state/src/Context";
import RSButton from "ds_legacy/components/RSButton";

export const parseAddress = (addressString: string | null) => {
  if (!addressString) return { houseNumber: null, street: null };

  const match = addressString.match(/^(.+?)\s+(\d+.*)$/);
  if (match) {
    return { houseNumber: match[2].trim(), street: match[1].trim() };
  }
  return { houseNumber: null, street: addressString };
};

export function GKVGeneralFormPresenter({
  auction,
  skipRequiredValidation,
}: {
  auction?: Auction;
  skipRequiredValidation: boolean;
}) {
  const translations = useTranslations();
  const livingSituationOptions = useLivingSituationOptions();
  const guardianOptions = useGuardianOptions();
  const { onStateChange } = useStateDispatcherContext();

  const handleProviderImport = () => {
    if (!auction?.winner?.careprovider || !onStateChange) return;

    const { careprovider } = auction.winner;
    const { address, central_phone_number, name } = careprovider;

    const { houseNumber, street } = address?.street
      ? { houseNumber: address.street_number, street: address.street }
      : parseAddress(address?.address ?? null);

    onStateChange(name ?? null, "prefered_clinic_name", null);
    onStateChange(street ?? null, "prefered_clinic_street", null);
    onStateChange(houseNumber ?? null, "prefered_clinic_house_number", null);
    onStateChange(
      address?.zip_code ?? null,
      "prefered_clinic_postal_code",
      null,
    );
    onStateChange(address?.city ?? null, "prefered_clinic_city", null);
    onStateChange(central_phone_number ?? null, "prefered_clinic_phone", null);
    onStateChange(
      auction.start_date ?? null,
      "prefered_clinic_contacted_start_date",
      null,
    );
  };

  return (
    <VerticalLayout width="100%">
      <FormElementRenderProps elementName="hospital_ik_number">
        {({ value }) => (
          <input name="hospital_ik_number" type="hidden" value={value ?? ""} />
        )}
      </FormElementRenderProps>
      <FormElementRenderProps elementName="insurance_ik_number">
        {({ value }) => (
          <input name="insurance_ik_number" type="hidden" value={value ?? ""} />
        )}
      </FormElementRenderProps>
      <FormSectionsWrapper>
        <Section>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="patient_first_name"
              label={translations.people.firstName}
              large
              required={!skipRequiredValidation}
            />
            <TextInputWithLabel
              bold
              elementName="patient_last_name"
              label={translations.people.lastName}
              large
              required={!skipRequiredValidation}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="patient_street"
              label={translations.address.street}
              large
            />
            <TextInputWithLabel
              bold
              elementName="patient_housenumber"
              label={translations.address.number}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="patient_zipcode"
              label={translations.address.zipcode}
            />
            <TextInputWithLabel
              bold
              elementName="patient_city"
              label={translations.address.city}
              large
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="patient_phone_number"
              label={translations.patientForms.generalForm.insuredPerson.phone}
              large
            />
            <DatePickerWithLabel
              bold
              elementName="patient_birthdate"
              label={
                translations.patientForms.generalForm.insuredPerson.dateOfBirth
              }
              required={!skipRequiredValidation}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="insurance_number"
              label={
                translations.patientForms.generalForm.insuredPerson
                  .insuranceNumber
              }
              required={!skipRequiredValidation}
            />
          </SectionRow>
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.generalForm.sectionI.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_18,
              fontWeight: FONT_WEIGHT_BOLD,
            }}
          >
            {translations.patientForms.generalForm.sectionI.titleNeedForCare}
          </Subheading>
          <YesNoRadioWithLabel
            elementName="insured_details_application_insurance_benefits"
            label={
              translations.patientForms.generalForm.sectionI.questionNeedForCare
            }
            required={!skipRequiredValidation}
          />
          <PatientFormSubheading>
            {
              translations.patientForms.generalForm.sectionI
                .titlePensionApplication
            }
          </PatientFormSubheading>
          <YesNoRadioWithLabel
            elementName="retirement_pension_applied"
            label={
              translations.patientForms.generalForm.sectionI
                .subtitleRetirementPension
            }
            required={!skipRequiredValidation}
          />
          <TextAreaWithLabel
            elementName="retirement_pension_company"
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            marginOverride={margin(0)}
          />
          <YesNoRadioWithLabel
            elementName="retirement_pension_earning_capacity_applied"
            formLabelSx={{ padding: padding(2, 0, 1) }}
            label={
              translations.patientForms.generalForm.sectionI
                .subtitlePensionReducedEarning
            }
            required={!skipRequiredValidation}
          />
          <TextAreaWithLabel
            elementName="retirement_earning_capacity_company"
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            marginOverride={margin(0)}
          />
          <PatientFormSubheading withBottomPadding>
            {
              translations.patientForms.generalForm.sectionI
                .questionLivingSituation
            }
          </PatientFormSubheading>
          <TextInputWithLabel
            elementName="living_lift_description"
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationFloor
            }
            labelWrapperSx={{ width: "100%" }}
            marginOverride={margin(0)}
            required={!skipRequiredValidation}
            style={{
              minWidth: SMALL_INPUT_MIN_WIDTH,
              width: SMALL_INPUT_WIDTH,
            }}
          />
          <YesNoRadioWithLabel
            elementName="living_lift"
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationElevator
            }
            required={!skipRequiredValidation}
          />
          <TextAreaWithLabel
            elementName="living_special"
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationSpecifications
            }
            marginOverride={margin(0)}
          />
          <PatientFormSubheading>
            {translations.patientForms.generalForm.sectionI.questionHomeCare}
          </PatientFormSubheading>
          <RadioGroupV2
            elementName="living_situation"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={{
              fontSize: FONT_SIZE_14,
              padding: padding(1, 0),
            }}
            label={
              translations.patientForms.generalForm.sectionI.subtitleHomeCare
            }
            options={livingSituationOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            required={!skipRequiredValidation}
            sideMutation={(_, mutate) =>
              mutate(null, "living_situation_at_home")
            }
          />
          <YesNoRadioWithLabel
            elementName="domestic_situation_is_safe"
            label={
              translations.patientForms.generalForm.sectionI
                .currentHomeCareEnsured
            }
            required={!skipRequiredValidation}
          />
          <PatientFormSubheading>
            {translations.patientForms.generalForm.sectionI.questionGp}
          </PatientFormSubheading>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              bold
              elementName="general_practitioner_first_name"
              label={translations.people.firstName}
              large
            />
            <TextInputWithLabel
              bold
              elementName="general_practitioner_last_name"
              label={translations.people.lastName}
              large
            />
          </SectionRow>
          <Body
            as="p"
            fontSize={FONT_SIZE_16}
            fontWeight={FONT_WEIGHT_BOLD}
            margin={margin(3, 0, 0)}
          >
            {translations.patientForms.generalForm.sectionI.subtitleAddressGp}
          </Body>
          <SectionRow customPadding={padding(2, 0, 0)}>
            <TextInputWithLabel
              bold
              elementName="general_practitioner_street"
              label={translations.address.street}
              large
            />
            <TextInputWithLabel
              bold
              elementName="general_practitioner_housenumber"
              label={translations.address.number}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="general_practitioner_zipcode"
              label={translations.patientForms.generalForm.sectionI.gpZipCode}
            />
            <TextInputWithLabel
              bold
              elementName="general_practitioner_city"
              label={translations.patientForms.generalForm.sectionI.gpCity}
              large
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="general_practitioner_phone_number"
              label={
                translations.patientForms.generalForm.sectionI.gpPhoneNumber
              }
              large
            />
          </SectionRow>
          <PatientFormSubheading>
            {translations.patientForms.generalForm.sectionI.questionGuardian}
          </PatientFormSubheading>
          <div className="flex flex-col space-y-4">
            {[GUARDIAN_1, GUARDIAN_2].map((num) => (
              <div
                key={num}
                className={clsx("flex flex-col", {
                  "pt-4": num === GUARDIAN_2,
                })}
              >
                <RadioGroupV2
                  elementName={`insured_details_guardian_${num}_type`}
                  formControlLabelSx={{
                    marginLeft: 0,
                    paddingBottom: sizing(0.5),
                  }}
                  formLabelSx={visuallyHidden as SxProps}
                  label={
                    translations.patientForms.generalForm.sectionI
                      .accessibilityGuardianType
                  }
                  options={guardianOptions}
                  radioGroupSx={{ flexDirection: "row", paddingTop: sizing(1) }}
                  radioSx={RADIO_WHITE_BACKGROUND}
                />
                <SectionRow customPadding={padding(1, 0, 0)}>
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_first_name`}
                    label={translations.people.firstName}
                    large
                  />
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_last_name`}
                    label={translations.people.lastName}
                    large
                  />
                </SectionRow>
                <Body
                  as="p"
                  fontSize={FONT_SIZE_16}
                  fontWeight={FONT_WEIGHT_BOLD}
                  margin={margin(3, 0, 0)}
                >
                  {
                    translations.patientForms.generalForm.sectionI
                      .subtitleAddressGp
                  }
                </Body>
                <SectionRow customPadding={padding(2, 0, 0)}>
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_street`}
                    label={translations.address.street}
                    large
                  />
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_house_number`}
                    label={translations.address.number}
                  />
                </SectionRow>
                <SectionRow>
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_zipcode`}
                    label={
                      translations.patientForms.generalForm.sectionI.gpZipCode
                    }
                  />
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_city`}
                    label={
                      translations.patientForms.generalForm.sectionI.gpCity
                    }
                    large
                  />
                </SectionRow>
                <SectionRow>
                  <TextInputWithLabel
                    bold
                    elementName={`insured_details_guardian_${num}_phone`}
                    label={
                      translations.patientForms.generalForm.sectionI
                        .gpPhoneNumber
                    }
                    large
                  />
                </SectionRow>
              </div>
            ))}
          </div>
          <PatientFormSubheading withBottomPadding>
            {
              translations.patientForms.generalForm.sectionI
                .questionSpecialRequirements
            }
          </PatientFormSubheading>
          <TextAreaWithLabel
            elementName="reason_for_reha_clinic"
            label={
              translations.patientForms.generalForm.sectionI
                .labelSpecialRequirements
            }
            marginOverride={margin(0)}
          />
        </Section>
        <Section>
          <PatientFormSubheading withBottomPadding>
            {translations.patientForms.generalForm.sectionI.additionalInfoNote}
          </PatientFormSubheading>
          <FormControl component="fieldset">
            <SubheadingFormLegend>
              {
                translations.patientForms.generalForm.sectionI
                  .signatureDeclaration
              }
            </SubheadingFormLegend>
            <FormGroup>
              <CheckboxInputField
                customValue={String(PREDICAMENT_STATE_YES)}
                elementName="declaration_of_intent_confirmed"
                flatModel
                label={
                  translations.patientForms.generalForm
                    .declarationOfIntentProvided
                }
                required={!skipRequiredValidation}
              />
            </FormGroup>
          </FormControl>
        </Section>
        <Section>
          <div className="mb-4 flex flex-col space-y-2">
            <PatientFormSubheading>
              {
                translations.patientForms.generalForm.sectionI
                  .consentToDataTransferHeading
              }
            </PatientFormSubheading>
            <p>
              {
                translations.patientForms.generalForm.sectionI
                  .consentToDataTransferText
              }
            </p>
          </div>
          <RadioGroupV2
            elementName="data_transfer_consent_given"
            formControlLabelSx={{ margin: margin(0, 0, 0, 0) }}
            formLabelSx={{
              fontWeight: FONT_WEIGHT_BOLD,
              margin: margin(0, 0, 0, 0.5),
            }}
            label={
              translations.patientForms.generalForm.sectionI
                .consentToDataTransferSignature
            }
            options={[
              {
                label:
                  translations.patientForms.generalForm.consentDataProvided,
                value: PREDICAMENT_STATE_YES,
              },
              {
                label:
                  translations.patientForms.generalForm.sectionTwo
                    .consentDataNotProvided,
                value: PREDICAMENT_STATE_NO,
              },
            ]}
            required={!skipRequiredValidation}
          />
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.generalForm.sectionTwo.title}
        </PatientFormsSectionHeader>
        <Section>
          <SectionRow>
            <TextInputWithLabel
              bold
              disabled
              elementName="site_id_number"
              label={translations.careseeker.siteIdNumber}
              large
              required={!skipRequiredValidation}
            />
            <TextInputWithLabel
              bold
              elementName="case_id"
              label={translations.patientForms.generalForm.sectionTwo.patientId}
              large
              required={!skipRequiredValidation}
            />
          </SectionRow>
          <PatientFormSubheading>
            {
              translations.patientForms.generalForm.sectionTwo
                .detailsSocialWorker
            }
          </PatientFormSubheading>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              bold
              elementName="social_worker_first_name"
              label={translations.people.firstName}
              large
            />
            <TextInputWithLabel
              bold
              elementName="social_worker_last_name"
              label={translations.people.lastName}
              large
              required={!skipRequiredValidation}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              bold
              elementName="social_worker_phone_number"
              label={
                translations.patientForms.generalForm.sectionTwo
                  .socialWorkerPhone
              }
              large
              required={!skipRequiredValidation}
            />
            <TextInputWithLabel
              bold
              elementName="social_worker_fax_number"
              label={
                translations.patientForms.generalForm.sectionTwo.socialWorkerFax
              }
              large
            />
          </SectionRow>
          <YesNoRadioWithLabel
            elementName="communication_in_german_possible"
            formLabelSx={patientSubheadingSx}
            label={
              translations.patientForms.generalForm.sectionTwo
                .communicationWithPatient
            }
            required={!skipRequiredValidation}
            sideMutation={(_value, mutate) => {
              mutate(null, "communication_other_language");
            }}
            subForms={{
              [PREDICAMENT_STATE_NO]: (
                <TextAreaWithLabel
                  elementName="communication_other_language"
                  flatModel
                  label={
                    translations.patientForms.generalForm.sectionTwo
                      .communicationNoGerman
                  }
                  marginOverride={margin(0)}
                />
              ),
            }}
          />
          <YesNoRadioWithLabel
            elementName="prefered_clinic_contacted"
            flatModel
            formLabelSx={patientSubheadingSx}
            label={
              translations.patientForms.generalForm.sectionTwo.costCoverage
            }
            required={!skipRequiredValidation}
            sideMutation={(_value, mutate) => {
              mutate(null, "prefered_clinic_name");
              mutate(null, "prefered_clinic_street");
              mutate(null, "prefered_clinic_house_number");
              mutate(null, "prefered_clinic_postal_code");
              mutate(null, "prefered_clinic_city");
              mutate(null, "prefered_clinic_phone");
              mutate(null, "prefered_clinic_contacted_start_date");
            }}
            startWithNo
            subForms={{
              [PREDICAMENT_STATE_YES]: (
                <div className="ml-8 space-y-4 py-4">
                  {auction?.winner && (
                    <RSButton
                      color="primary"
                      id="import-provider-from-recare"
                      LeftIcon={DownloadIcon}
                      loading="na"
                      variant="outlined"
                      onClick={handleProviderImport}
                    >
                      {
                        translations.patientForms.generalForm
                          .importProviderInformation
                      }
                    </RSButton>
                  )}
                  <div className="flex space-x-8">
                    <TextInputWithLabel
                      bold
                      elementName="prefered_clinic_name"
                      flatModel
                      label={translations.institution.name}
                      large
                      marginOverride={margin(0)}
                      required={!skipRequiredValidation}
                    />
                  </div>
                  <div className="flex space-x-8">
                    <TextInputWithLabel
                      bold
                      elementName="prefered_clinic_street"
                      flatModel
                      label={translations.address.street}
                      large
                      marginOverride={margin(0)}
                      required={!skipRequiredValidation}
                    />
                    <TextInputWithLabel
                      bold
                      elementName="prefered_clinic_house_number"
                      flatModel
                      label={translations.address.number}
                      marginOverride={margin(0)}
                      required={!skipRequiredValidation}
                    />
                  </div>
                  <div className="flex space-x-8">
                    <TextInputWithLabel
                      bold
                      elementName="prefered_clinic_postal_code"
                      flatModel
                      label={translations.address.zipcode}
                      marginOverride={margin(0)}
                      required={!skipRequiredValidation}
                    />
                    <TextInputWithLabel
                      bold
                      elementName="prefered_clinic_city"
                      flatModel
                      label={translations.address.city}
                      large
                      marginOverride={margin(0)}
                      required={!skipRequiredValidation}
                    />
                  </div>
                  <div className="flex space-x-8">
                    <TextInputWithLabel
                      bold
                      elementName="prefered_clinic_phone"
                      flatModel
                      label={translations.contactInformation.phoneNumber}
                      large
                      marginOverride={margin(0)}
                      required={!skipRequiredValidation}
                    />
                  </div>
                  <div className="flex space-x-8">
                    <DatePickerWithLabel
                      bold
                      elementName="prefered_clinic_contacted_start_date"
                      flatModel
                      inputSx={{ width: SMALL_INPUT_WIDTH }}
                      label={
                        translations.patientForms.generalForm.sectionTwo
                          .rehabIntakeDate
                      }
                      required={!skipRequiredValidation}
                      wrapperSx={{ width: "100%" }}
                    />
                  </div>
                </div>
              ),
            }}
          />
        </Section>
      </FormSectionsWrapper>
    </VerticalLayout>
  );
}
